import {
	createUser,
	getDateTimes,
	getLanguages,
	getRoles,
	getPermissionGroups,
	getUsernames,
	getUser,
} from '../../../api';
import { onRequest } from '../..';
import {
	PropertyType,
	ActionsType,
	debounce,
	CUSTOM_ROLE,
	DEFAULT_PERMISSIONS,
	PROFILE_SETTINGS_URL,
} from '../../../common';

const userInit = async (user: PropertyType) => {
	const { partners } = user;
	const { dateTimes } = await getDateTimes();
	const { languages } = await getLanguages();

	return {
		partners,
		dateTimes,
		languages,
		activeLanguage: [languages[0]],
		activeDateTime: [dateTimes[2]],
	};
};

const rolesInit = async (data: PropertyType) => {
	const dataRoles = await getRoles(data);
	const rolePermissions: PropertyType = {};
	const roles: PropertyType[] = [];

	dataRoles.forEach((r: PropertyType) => {
		rolePermissions[r.name] = r.permissions;
		roles.push({ label: r.name, value: r.name });
	});

	return { rolePermissions, roles };
};

const usersInit = async (payload: PropertyType) => {
	const data = await getUsernames(payload);

	return { users: data };
};

const permissionsInit = async (token: string) => {
	const data = await getPermissionGroups({ token });

	return { permissionGroups: data };
};

const userSubmit = async (state: PropertyType, payload: PropertyType) => {
	const { token, history } = payload;
	const { user_id, success } = await createUser({ token, ...state });

	debounce(() => history.replace(`${PROFILE_SETTINGS_URL.value}/${user_id}`), 3000);

	return { globalErrors: [], globalSuccess: success };
};

const getSettings = (activeSettings: string[], setting: string) => {
	const settingIndex = activeSettings.indexOf(setting);

	settingIndex > -1 ? activeSettings.splice(settingIndex, 1) : activeSettings.push(setting);

	return activeSettings;
};

const onPermissionsCopy = async (state: PropertyType, payload: string) => {
	const { activeUsers } = state;
	const { permissions, roles } = await getUser({
		token: payload,
		uid: activeUsers[0].value,
	});

	return {
		activePermissions: Object.keys(permissions),
		activeRoles: roles,
		customRole: roles[0].value === 'Custom',
	};
};

const onPermissionsUpdate = (state: PropertyType, payload: string) => {
	const { activePermissions } = state;
	const permissions = getSettings(activePermissions, payload);

	return {
		activePermissions: permissions,
		activeRoles: [],
		customRole: true,
	};
};

const onAllPermissionsUpdate = (state: PropertyType, payload: string) => {
	const activePermissions = payload;

	return { activePermissions, activeRoles: [], customRole: true };
};

const onRolesUpdate = (state: PropertyType, payload: PropertyType[]) => {
	const { rolePermissions } = state;
	const activePermissions: string[] = [...DEFAULT_PERMISSIONS];
	const activeRoles = payload.filter((r) => r.value !== CUSTOM_ROLE.value);
	const customRole = !activeRoles.length;

	activeRoles.forEach((r: PropertyType) => {
		rolePermissions[r.value].forEach((p: string) => !activePermissions.includes(p) && activePermissions.push(p));
	});

	return { activeRoles, activePermissions, customRole };
};

const onGroupOpen = (state: PropertyType, payload: string) => {
	const { openedGroups } = state;

	openedGroups.push(payload);

	return { openedGroups };
};

const onGroupClose = (state: PropertyType, payload: string) => {
	const { openedGroups } = state;
	const groupIndex = openedGroups.indexOf(payload);

	openedGroups.splice(groupIndex, 1);

	return { openedGroups };
};

export const actions: ActionsType = {
	USERNAME: (state, payload) => ({ username: payload }),
	USERNAME_INVALID: (state, payload) => ({ usernameInvalid: payload }),
	EMAIL: (state, payload) => ({ email: payload }),
	EMAIL_INVALID: (state, payload) => ({ emailInvalid: payload }),
	PASS: (state, payload) => ({ password: payload }),
	PASS_FAIR: (state, payload) => ({ passwordFair: payload }),
	PASS_STRONG: (state, payload) => ({ passwordStrong: payload }),
	LANGUAGE: (state, payload) => ({ activeLanguage: payload }),
	DATE_TIME: (state, payload) => ({ activeDateTime: payload }),
	ROLES_INIT: (state, payload) => onRequest(() => rolesInit(payload), true),
	ROLES_UPDATE: (state, payload) => onRolesUpdate(state, payload),
	USERS_INIT: (state, payload) => onRequest(() => usersInit(payload), true),
	USERS_UPDATE: (state, payload) => ({ activeUsers: payload }),
	PERMISSIONS_INIT: (state, payload) => onRequest(() => permissionsInit(payload), true),
	PERMISSIONS_COPY: (state, payload) => onRequest(() => onPermissionsCopy(state, payload), true),
	PERMISSIONS_UPDATE: (state, payload) => onPermissionsUpdate(state, payload),
	ALL_PERMISSIONS_UPDATE: (state, payload) => onAllPermissionsUpdate(state, payload),
	GROUP_OPEN: (state, payload) => onGroupOpen(state, payload),
	GROUP_CLOSE: (state, payload) => onGroupClose(state, payload),
	IMAGE_UPDATE: (state, payload) => ({ image: payload }),
	IMAGE_PREVIEW: (state, payload) => ({ imagePreview: payload }),
	GLOBAL_ERROR: (state, payload) => ({ globalErrors: payload }),
	GLOBAL_SUCCESS: (state, payload) => ({ globalSuccess: payload }),
	USER_INIT: (state, payload) => onRequest(() => userInit(payload), true),
	PARTNERS_UPDATE: (state, payload) => ({ activePartners: payload }),
	USER_SUBMIT: (state, payload) => onRequest(() => userSubmit(state, payload), true),
	MESSAGE_INVALIDATE: () => ({ globalErrors: [], globalSuccess: '' }),
};
